import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import {
  School,
  Star,
  People,
  Delete,
  Edit,
  PlayArrow,
} from "@mui/icons-material";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import Banner from "../Components/CourseDetailPage/Banner";
import Learnyou from "../Components/CourseDetailPage/Learnyou";
import CourseSection from "../Components/CourseDetailPage/CourseViewer";
import Reviews from "../Components/CourseDetailPage/Reviews";
import StudentsSection from "../Components/CourseDetailPage/StudentsSection";
import AddMaterial from "../Components/CourseDetailPage/AddMaterial";
import EditCourseModal from "../Components/CourseDetailPage/EditCourseModal";
import StudentSidebar from "./StudentSdebar";

const GOLD = "#D4AF37";

export default function CourseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // State
  const [course, setCourse] = useState({ parts: [] });
  const [materials, setMaterials] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [students, setStudents] = useState([]);
  const [isInstructor, setInstructor] = useState(false);
  const [isEnrolled, setEnrolled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setEditModal] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("jwttoken");
  const API_BASE_URL = "https://tdtlworld.com/dss-kaas-backend/";

  const fetchAllData = useCallback(async () => {
    if (!token) {
      navigate("/db-shiksha/kaas/log");
      return;
    }
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Course Details
      const courseRes = await axios.get(
        `${API_BASE_URL}lms/courses/${id}/`,
        config,
      );
      setCourse(courseRes.data);
      setInstructor(courseRes.data.display.is_instructor);
      setEnrolled(courseRes.data.display.is_enrolled);

      // Materials
      const materialsRes = await axios.get(
        `${API_BASE_URL}lms/courses/${id}/materials/`,
        config,
      );
      setMaterials(materialsRes.data.materials || []);

      // Reviews
      const reviewsRes = await axios.get(
        `${API_BASE_URL}lms/review/${id}/`,
        config,
      );
      setReviews(reviewsRes.data.reviews || []);

      // Students (only for instructor)
      if (courseRes.data.display.is_instructor) {
        const studentsRes = await axios.get(
          `${API_BASE_URL}lms/courses/${id}/students/`,
          config,
        );
        setStudents(studentsRes.data.students || []);
      }
    } catch (err) {
      console.error("Error fetching course data:", err);
      // Swal.fire("Error", "Could not load course details.", "error");
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData, showModal, showEditModal]);

  const enrollStudent = async () => {
    if (isEnrolled) {
      Swal.fire({
        title: "Already Enrolled",
        text: "You're already enrolled in this course.",
        icon: "info",
        confirmButtonColor: GOLD,
      });
      return;
    }
    try {
      setLoading(true);
      await axios.post(
        `${API_BASE_URL}lms/enroll/${id}/`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setEnrolled(true);
      Swal.fire({
        title: "Success!",
        text: "Enrollment Successful",
        icon: "success",
        confirmButtonColor: GOLD,
      }).then(() => {
        navigate("/db-shiksha/kaas/login");
      });
      fetchAllData();
    } catch (err) {
      console.error("Enrollment error:", err);
      Swal.fire({
        title: "Enrollment Failed",
        text: err.response?.data?.error || "Could not enroll.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !course.title) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "background.default" }}>
      <Header />

      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: "calc(100vh - 64px)",
            overflowY: "auto",
            bgcolor: "background.default",
            pb: 4,
            transition: theme.transitions.create("width", { duration: 300 }),
            width: sidebarExpanded ? "calc(100% - 280px)" : "calc(100% - 80px)",
          }}
        >
          <Banner
            course={course}
            isEnrolled={isEnrolled}
            isInstructor={isInstructor}
            trailer={course.trailer}
            lessonCount={course.parts?.length || 0}
            onEnroll={enrollStudent}
          />

          <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Stack spacing={6}>
              {/* Key Learning Points */}
              <Learnyou courseKeyPoints={course.course_key_points} />

              {/* Course Viewer (Student/Instructor only) */}
              {(isEnrolled || isInstructor) && (
                <Box
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider"
                  }}
                >
                  <CourseSection />
                </Box>
              )}

              {/* Instructor - Students Section */}
              {isInstructor && (
                <Box>
                  <Typography variant="h5" fontWeight={900} sx={{ mb: 4, letterSpacing: "-0.5px" }}>
                    Student Enrollment
                  </Typography>
                  <StudentsSection students={students} />
                </Box>
              )}

              {/* Reviews Section */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                  <Box sx={{ width: 4, height: 28, bgcolor: '#D4AF37', borderRadius: 2 }} />
                  <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: "-0.5px", color: "text.primary" }}>
                    What Our Students Say
                  </Typography>
                </Stack>
                <Reviews reviews={reviews} />
              </Box>
            </Stack>
          </Container>
          
          <Box sx={{ mt: 10 }}>
            <Footer />
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      <AddMaterial
        course_id={id}
        show={showModal}
        handleClose={() => setShowModal(false)}
      />
      <EditCourseModal
        course_id={id}
        show={showEditModal}
        handleClose={() => setEditModal(false)}
      />
    </Box>
  );
}
